import React from 'react'
import { Link } from 'react-router-dom'

const sections = [
  {
    title: '1. Introduction',
    body: [
      'Medpool connects patients with vetted hospitals and healthcare providers. This Privacy Policy explains how we collect, use, and protect the personal information of patients, hospitals, administrators, and visitors who interact with our platforms (web, mobile, or APIs).',
      'By using Medpool, you agree to the practices described below. If you disagree with any part of this policy, please discontinue using our services.'
    ]
  },
  {
    title: '2. Information We Collect',
    body: [
      'Account Data: name, email, phone number, address, role (patient or hospital), organization details, regulatory documents, and verification status.',
      'Health Request Data: descriptions of medical needs, uploaded medical reports, preferred hospitals, state and local government information, and supporting files voluntarily provided by patients.',
      'Payment & Wallet Data: transaction references, billing details, payout information (bank, account number, account name), and wallet balances.',
      'Usage & Device Data: log files, cookies, IP address, browser type, session duration, referral URLs, device identifiers, and actions taken on the platform.',
      'Support & Communications: messages sent to our support team, responses to surveys, call recordings (if applicable), and email interactions.'
    ]
  },
  {
    title: '3. How We Use Your Information',
    body: [
      'Provide Services: create and manage accounts, match patients with hospitals, process payments, manage payouts, and deliver platform features.',
      'Security & Compliance: authenticate logins, prevent fraud, monitor suspicious activity, comply with health and financial regulations, and enforce our terms of service.',
      'Communication: send transactional notifications (password resets, verification, payment updates), service announcements, and customer support responses.',
      'Product Improvement: analyze aggregated usage data, run diagnostics, improve performance, test new features, and personalize experiences.',
      'Legal Obligations: respond to lawful requests, resolve disputes, and protect the rights, property, or safety of Medpool, users, or the public.'
    ]
  },
  {
    title: '4. Legal Grounds for Processing (GDPR/NDPR)',
    body: [
      'Consent: when you sign up, submit medical requests, or opt into marketing communications.',
      'Contractual Necessity: to deliver the services you request, including patient-hospital matching, payments, and wallet operations.',
      'Legitimate Interest: to maintain platform integrity, improve security, and provide customer support.',
      'Legal Compliance: to meet financial, healthcare, or governmental reporting requirements.'
    ]
  },
  {
    title: '5. Data Sharing & Disclosure',
    body: [
      'Healthcare Providers: limited patient information is shared with hospitals to deliver requested medical services.',
      'Payment Processors: transaction data may be shared with payment gateways, banks, payout partners, and fraud prevention tools.',
      'Service Providers: trusted vendors who help with infrastructure, analytics, cloud storage, email, or customer tools (all bound by confidentiality agreements).',
      'Regulators & Law Enforcement: only when legally required or to protect vital interests.'
    ]
  },
  {
    title: '6. Data Retention',
    body: [
      'We retain personal data for as long as your account is active or as needed to provide services. Once deleted, we may still keep limited data to fulfill legal obligations, resolve disputes, prevent fraud, or enforce agreements.',
      'Inactive accounts may be anonymized or removed after a reasonable retention period that meets legal and operational requirements.'
    ]
  },
  {
    title: '7. Your Rights & Choices',
    body: [
      'Access & Correction: request a copy of your data or update inaccurate information from within your profile or by contacting support.',
      'Deletion: request deletion of your account and associated data (some information may remain for compliance purposes).',
      'Withdrawal of Consent: opt out of marketing emails or revoke permissions through account settings.',
      'Data Portability: request export of your personal data in a structured format.',
      'Complaints: if you believe your privacy rights were violated, contact us and/or the appropriate data protection authority.'
    ]
  },
  {
    title: '8. Security Measures',
    body: [
      'We implement administrative, technical, and physical safeguards such as encryption in transit, role-based access, secure cookie handling, and continuous monitoring.',
      'Despite best efforts, no security system is impenetrable. Users are encouraged to use strong passwords, enable available security features, and contact us immediately if they suspect unauthorized activity.'
    ]
  },
  {
    title: '9. International Data Transfers',
    body: [
      'Medpool may store or process data on servers located in different countries. Whenever data crosses borders, we ensure adequate protections and legal mechanisms (such as Standard Contractual Clauses) are in place.'
    ]
  },
  {
    title: '10. Children’s Privacy',
    body: [
      'Medpool is not intended for children under 16. If we discover that a child has provided personal information without verified parental consent, we will delete it promptly.'
    ]
  },
  {
    title: '11. Updates to This Policy',
    body: [
      'We may update this Privacy Policy to reflect changes in laws, regulations, or platform features. When updates occur, we will revise the “Last Updated” date and, when necessary, notify users through the platform or email.'
    ]
  },
  {
    title: '12. Contact Us',
    body: [
      'For privacy-related questions, requests, or complaints, contact our Data Protection team:',
      'Email: privacy@medpool.health',
      'Address: Medpool HQ, 42 Healthcare Drive, Lagos, Nigeria.'
    ]
  }
]

const PrivacyPolicy = () => {
  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-10">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
        </div>

        <div className="rounded-2xl bg-white p-8 shadow-lg">
          <div className="mb-10 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-1 text-xs font-semibold text-gray-600">
              Updated {new Date().toLocaleDateString()}
            </div>
            <h1 className="mt-4 text-3xl font-bold text-gray-900">Medpool Privacy Policy</h1>
            <p className="mt-3 text-base text-gray-600">
              This document explains how Medpool collects, uses, stores, and protects your personal data.
            </p>
          </div>

          <div className="space-y-8">
            {sections.map(section => (
              <section key={section.title}>
                <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                <div className="mt-3 space-y-3 text-sm text-gray-700 leading-relaxed">
                  {section.body.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-10 rounded-xl bg-gray-50 p-6 text-sm text-gray-700">
            <h3 className="mb-2 font-semibold text-gray-900">Questions?</h3>
            <p>
              If you have any questions about this policy or how we handle your data, please contact us at{' '}
              <a href="mailto:privacy@medpool.health" className="text-blue-600 hover:underline">privacy@medpool.health</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy


